import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Self-healing hook: when content is opened and has stale pending statuses,
 * triggers the appropriate background processing pipeline.
 * Runs once per content ID per mount.
 */
export function useContentSelfHeal(contentId: string | undefined, contentType: string | undefined) {
  const triggered = useRef<string | null>(null);

  useEffect(() => {
    if (!contentId || !contentType || triggered.current === contentId) return;

    async function check() {
      const { data, error } = await supabase
        .from("content")
        .select("id, type, source_url, storage_path, pdf_storage_path, conversion_status, transcript_status, text_extraction_status, chapters_status")
        .eq("id", contentId!)
        .single();

      if (error || !data || !data.source_url) return;

      const isMedia = ["youtube", "audio", "video"].includes(data.type);
      const isDocument = ["pdf", "web", "doc", "text"].includes(data.type);

      // YouTube/audio/video with pending transcript and no transcripts yet
      if (isMedia && data.transcript_status === "pending") {
        const { count } = await supabase
          .from("media_transcripts")
          .select("*", { count: "exact", head: true })
          .eq("content_id", data.id);

        if (!count || count === 0) {
          triggered.current = contentId!;
          supabase.functions.invoke("extract-media-transcript", {
            body: { contentId: data.id },
          }).catch(err => console.error("Self-heal transcript failed:", err));
          return;
        }
      }

      // Document needing conversion (no file in storage yet)
      if (isDocument && !data.storage_path && !data.pdf_storage_path && data.conversion_status !== "completed") {
        triggered.current = contentId!;
        supabase.functions.invoke("convert-content-to-pdf", {
          body: { contentId: data.id, type: data.type, sourceUrl: data.source_url },
        }).catch(err => console.error("Self-heal conversion failed:", err));
        return;
      }

      // Document with file in storage but text not extracted
      if (isDocument && (data.storage_path || data.pdf_storage_path) && data.text_extraction_status === "pending") {
        const { count } = await supabase
          .from("document_text")
          .select("*", { count: "exact", head: true })
          .eq("content_id", data.id);

        if (!count || count === 0) {
          triggered.current = contentId!;
          supabase.functions.invoke("extract-document-text", {
            body: { contentId: data.id },
          }).catch(err => console.error("Self-heal text extraction failed:", err));
          return;
        }
      }
    }

    check();
  }, [contentId, contentType]);
}
